import { ConnectError, Code } from '@connectrpc/connect';
import { schemaService, searchService, workQueueService } from '@/core/api/services';
import type { WorkItem } from '@hub/proto/workqueue/v1/workqueue_pb';
import {
  PHI_RESTRICTED_SENTINEL,
  type BladeDataSource,
  type BladeListData,
  type BladeEntityData,
  type BladeField,
  type BladeSearchData,
  type ViewDefinition,
} from '../BladeDataSource';
import type { BladeDescriptor } from '../types';

// Live @hub/api source (HUB-277) — binds view-primitives to the WorkQueueService.
//
// PHI: work-item tags/payloads may carry PHI. This reads them for display to an
// authorized workbench user (that's the product), but MUST NOT log or persist
// them, and only the work-item id (an identifier, not a payload) appears in query
// keys / URLs. Permission failures surface as the "denied" state (see
// BladeDataSource.statusForError). PHI-Touching — needs security review before merge.

/** A human title for a work item without dumping arbitrary payload: prefer an
 * explicit display tag, else fall back to type + id. Display tags are NOT on the
 * server's operational allowlist (they may carry names), so for uncleared
 * callers they arrive as the restricted sentinel — fall through to type · id
 * rather than showing the marker. */
function titleFor(item: WorkItem): string {
  const display = item.tags?.display || item.tags?.title;
  if (display && display !== PHI_RESTRICTED_SENTINEL) return display;
  return `${item.type || 'Item'} · ${item.id}`;
}

function metaFor(item: WorkItem): string {
  return [item.status, item.type].filter(Boolean).join(' · ');
}

function formatCreated(item: WorkItem): string {
  const ts = item.createdAt;
  if (!ts) return '—';
  return new Date(Number(ts.seconds) * 1000).toLocaleDateString();
}

export const liveSource: BladeDataSource = {
  async fetchList(): Promise<BladeListData> {
    const res = await workQueueService.listWorkItems({
      statuses: [],
      types: [],
      tags: {},
      claimedBy: '',
      sortBy: '',
      sortDesc: false,
      pageSize: 50,
      pageToken: '',
    });
    const rows = (res.items ?? []).map((item) => ({
      id: item.id,
      title: titleFor(item),
      meta: metaFor(item),
    }));
    return { rows };
  },

  async fetchEntity(descriptor: BladeDescriptor): Promise<BladeEntityData> {
    const res = await workQueueService.getWorkItem({ id: descriptor.entityId });
    const item = res.item;
    if (!item) return { title: descriptor.entityId ?? 'Record', fields: [] };

    // `key` is the canonical field name a ViewDefinition matches on (HUB-408).
    const fields: BladeField[] = [
      { key: 'type', label: 'Type', value: item.type || '—' },
      { key: 'status', label: 'Status', value: item.status || '—' },
      { key: 'claimed_by', label: 'Claimed by', value: item.claimedBy || 'Unassigned' },
      { key: 'created', label: 'Created', value: formatCreated(item) },
      // Tags may carry domain (PHI) fields. The server redacts identifier
      // values for callers without phi:view-identifiers (HUB-357) — map the
      // sentinel to a restricted field so the UI renders a placeholder. Tag keys
      // are their own canonical field names.
      ...Object.entries(item.tags ?? {}).map(([label, value]) =>
        value === PHI_RESTRICTED_SENTINEL
          ? { key: label, label, value: '', restricted: true }
          : { key: label, label, value },
      ),
    ];

    return {
      title: titleFor(item),
      badge: item.status || undefined,
      fields,
    };
  },

  // Config-driven view/form definitions (HUB-408): resolve the ACTIVE server-side
  // `view` definition for a view key so the record primitive renders its field
  // layout from data rather than hardcoded TSX. Layout metadata only (no PHI). A
  // NotFound (no such active view) resolves to null so the primitive keeps its
  // hardcoded fallback — zero regression while views are authored.
  async fetchViewDefinition(viewKey: string): Promise<ViewDefinition | null> {
    try {
      const res = await schemaService.getViewDefinition({ viewKey, status: '' });
      const v = res.view;
      if (!v) return null;
      return {
        id: v.id,
        label: v.label,
        primitive: v.primitive,
        entityType: v.entityType || undefined,
        sections: (v.sections ?? []).map((s) => ({
          id: s.id,
          label: s.label || undefined,
          fields: (s.fields ?? []).map((f) => ({
            field: f.field,
            label: f.label || undefined,
            as: f.as || undefined,
            access: f.access || undefined,
          })),
        })),
      };
    } catch (e) {
      if (e instanceof ConnectError && e.code === Code.NotFound) return null;
      throw e;
    }
  },

  // Global search (HUB-281/HUB-369): the server-side index does the matching;
  // hits are identity + relevance only. Query text is sent to the RPC and kept
  // out of URLs/logs (the results blade is ephemeral — F3). Titles come from
  // indexed content and may arrive as the PHI sentinel for uncleared callers
  // (HUB-357 governs redaction server-side; no client-side masking here).
  async fetchSearch(descriptor: BladeDescriptor): Promise<BladeSearchData> {
    const text = (descriptor.params?.q ?? '').trim();
    const res = await searchService.search({
      text,
      types: [],
      filters: [],
      limit: 50,
      offset: 0,
    });
    return {
      hits: (res.hits ?? []).map((h) => ({ id: h.id, type: h.type, title: h.title })),
    };
  },
};
